import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Checkbox, Icon, InputWrapper, Link, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const FieldWrapper = memo(
    ({
        canClear,
        children,
        className,
        description,
        error,
        info,
        isBulkAction,
        isDuplicate,
        isFullWidth = true,
        label,
        labelMode,
        mandatory,
        onClear,
        onReset,
        toDelete = false,
        willBeCleared,
    }) => {
        const theme = useTheme();
        const [bulkEditable, setBulkEditable] = useState(false);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const willBeClearedContent = useMemo(() => {
            if (!Array.isArray(willBeCleared)) return null;

            if (willBeCleared.length === 1) {
                return (
                    <>
                        <strong>{willBeCleared[0]}</strong>.
                    </>
                );
            }

            return (
                <>
                    {willBeCleared.map((item, index) => (
                        <span key={item}>
                            <strong>{item}</strong>
                            {index < willBeCleared.length - 1 ? ', ' : '.'}
                        </span>
                    ))}
                </>
            );
        }, [willBeCleared]);

        const rootClassName = useMemo(
            () =>
                classnames('fm-field-wrapper', className, {
                    'fm-field-wrapper--duplicate': isDuplicate,
                }),
            [className, isDuplicate],
        );

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const openBulkEdit = useCallback(() => {
            setBulkEditable(true);
        }, []);

        const handleClear = useCallback(
            ({ reset = false } = {}) => {
                const clear = reset ? false : !toDelete;
                onClear?.(clear);
                if (clear && onReset) onReset(null);
            },
            [onClear, onReset, toDelete],
        );

        const handleReset = useCallback(() => {
            onReset?.(null);
            handleClear({ reset: true });
            setBulkEditable(false);
        }, [handleClear, onReset]);

        // ========================================
        // RENDER
        // ========================================

        return (
            <InputWrapper
                className={rootClassName}
                label={label}
                labelMode={labelMode}
                isRequired={!isBulkAction && mandatory}
                hint={description}
                error={error}
                info={info}
                isFullWidth={isFullWidth}
            >
                {isBulkAction && !bulkEditable ? (
                    <Text
                        className="fm-field-wrapper__bulk-placeholder"
                        color={theme.colors.utility.textDisabled}
                        onClick={openBulkEdit}
                    >
                        {getLiteral('placeholder_click_to_edit')}
                    </Text>
                ) : (
                    <div className="fm-field-wrapper__content">
                        {children}
                        {isBulkAction && bulkEditable && (
                            <>
                                {Array.isArray(willBeCleared) && (
                                    <Text
                                        className="fm-field-wrapper__bulk-warning"
                                        color={theme.colors.semantic.negative450}
                                        type="caption"
                                    >
                                        {getLiteral('label_parent_field_with_dependencies')}
                                        <Tooltip content={willBeClearedContent}>
                                            <span>
                                                <Icon
                                                    size="small"
                                                    name="info"
                                                    color={theme.colors.semantic.negative450}
                                                />
                                            </span>
                                        </Tooltip>
                                    </Text>
                                )}
                                <div className="fm-field-wrapper__bulk-actions">
                                    {canClear && (
                                        <div
                                            className="fm-field-wrapper__bulk-clear"
                                            onClick={handleClear}
                                        >
                                            <Checkbox
                                                checked={toDelete}
                                                color="actionMajor"
                                                size="small"
                                            />
                                            <Text type="caption">
                                                {getLiteral('label_delete_current_values')}
                                            </Text>
                                        </div>
                                    )}
                                    <Link variation="primary" type="caption" onClick={handleReset}>
                                        {getLiteral('action_cancel')}
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </InputWrapper>
        );
    },
);

FieldWrapper.displayName = 'FieldWrapper';

FieldWrapper.propTypes = {
    canClear: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    description: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    info: PropTypes.string,
    isBulkAction: PropTypes.bool,
    isDuplicate: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
    mandatory: PropTypes.bool,
    onClear: PropTypes.func,
    onReset: PropTypes.func,
    toDelete: PropTypes.bool,
    willBeCleared: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

export default FieldWrapper;
